import { useEffect, useState } from "react";
import { Briefcase, Building2, FileText, Send, Upload, CheckCircle2, ListTodo } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { CadastrosManager } from "./CadastrosManager";

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.toString().split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ComercialPage() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [tab, setTab] = useState("new");
  const [saving, setSaving] = useState(false);
  const [contractFile, setContractFile] = useState(null);
  const [contractFilePreview, setContractFilePreview] = useState(null);

  const [clientForm, setClientForm] = useState({
    name: "", type: "prefeitura", city: "", state: "", cnpj: "",
    contact_name: "", contact_email: "", contact_phone: "",
  });

  const [contractForm, setContractForm] = useState({
    client_id: "", product_id: "", start_date: "", end_date: "",
    contract_value: "", notes: "",
  });

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/clients").then(({ data }) => setClients(data)).catch(() => {});
  }, []);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Arquivo muito grande (máx 10MB).");
      return;
    }
    setContractFile(f);
    setContractFilePreview({ name: f.name, size: (f.size / 1024).toFixed(1) + " KB" });
  };

  const validateClient = () => {
    const errors = [];
    if (!clientForm.name.trim() || clientForm.name.trim().length < 2) errors.push("Nome do cliente");
    if (!clientForm.type.trim()) errors.push("Tipo");
    if (!clientForm.city.trim()) errors.push("Cidade");
    if (!clientForm.state.trim() || clientForm.state.trim().length !== 2) errors.push("UF (2 letras)");
    if (!clientForm.cnpj.trim() || clientForm.cnpj.replace(/\D/g, "").length < 14) errors.push("CNPJ (14 dígitos)");
    if (!clientForm.contact_name.trim()) errors.push("Nome do contato");
    if (!clientForm.contact_email.trim() || !EMAIL_RE.test(clientForm.contact_email.trim())) errors.push("E-mail do contato");
    if (!clientForm.contact_phone.trim() || clientForm.contact_phone.replace(/\D/g, "").length < 8) errors.push("Telefone do contato");
    if (!contractFile) errors.push("Contrato assinado (anexo)");
    return errors;
  };

  const submitClient = async (e) => {
    e.preventDefault();
    const errors = validateClient();
    if (errors.length) {
      toast.error(`Preencha todos os campos: ${errors.join(", ")}`);
      return;
    }
    setSaving(true);
    try {
      const b64 = await fileToBase64(contractFile);
      const body = {
        ...clientForm,
        contract_file_name: contractFile.name,
        contract_file_data: b64,
        contract_file_size: contractFile.size,
      };
      const { data } = await api.post("/clients", body);
      toast.success("Cliente cadastrado. Secretária e Diretora notificadas.");
      setClients((prev) => [...prev, data]);
      setContractForm((prev) => ({ ...prev, client_id: data.id }));
      setTab("contract");
      setClientForm({
        name: "", type: "prefeitura", city: "", state: "", cnpj: "",
        contact_name: "", contact_email: "", contact_phone: "",
      });
      setContractFile(null);
      setContractFilePreview(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao cadastrar cliente.");
    } finally {
      setSaving(false);
    }
  };

  const submitContract = async (e) => {
    e.preventDefault();
    if (!contractForm.client_id || !contractForm.product_id || !contractForm.start_date || !contractForm.end_date) {
      toast.error("Preencha todos os campos obrigatórios.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        ...contractForm,
        contract_value: contractForm.contract_value ? Number(contractForm.contract_value) : null,
        start_date: new Date(contractForm.start_date).toISOString(),
        end_date: new Date(contractForm.end_date).toISOString(),
      };
      await api.post("/contracts", body);
      toast.success("Contrato cadastrado! Encaminhado à Secretária.");
      setContractForm({
        client_id: "", product_id: "", start_date: "", end_date: "", contract_value: "", notes: "",
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao cadastrar contrato.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Novo Cadastro</h1>
        <p className="text-sm text-slate-600 mt-1">
          Registre um novo cliente (com contrato assinado anexo) e o respectivo contrato.
          <span className="ml-1 text-red-600 font-medium">Todos os campos são obrigatórios.</span>
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="new" data-testid="tab-new-client">
            <Building2 className="w-4 h-4 mr-2" /> 1. Cadastrar Cliente
          </TabsTrigger>
          <TabsTrigger value="contract" data-testid="tab-new-contract">
            <FileText className="w-4 h-4 mr-2" /> 2. Cadastrar Contrato
          </TabsTrigger>
          <TabsTrigger value="existing" data-testid="tab-existing-registers">
            <ListTodo className="w-4 h-4 mr-2" /> 3. Cadastros existentes
          </TabsTrigger>
        </TabsList>

        <TabsContent value="new">
          <Card className="card-soft">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="w-4 h-4 text-midnight" />
                Dados do Município / Órgão Cliente
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={submitClient} className="grid sm:grid-cols-2 gap-4" noValidate>
                <div className="sm:col-span-2">
                  <Label>Nome do cliente <span className="text-red-600">*</span></Label>
                  <Input value={clientForm.name} onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
                    placeholder="Ex.: Prefeitura Municipal de Cidade X" required className="mt-1" data-testid="client-name-input" />
                </div>
                <div>
                  <Label>Tipo <span className="text-red-600">*</span></Label>
                  <Select value={clientForm.type} onValueChange={(v) => setClientForm({ ...clientForm, type: v })}>
                    <SelectTrigger className="mt-1" data-testid="client-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="prefeitura">Prefeitura</SelectItem>
                      <SelectItem value="camara">Câmara Municipal</SelectItem>
                      <SelectItem value="outro">Outro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>CNPJ <span className="text-red-600">*</span></Label>
                  <Input value={clientForm.cnpj} onChange={(e) => setClientForm({ ...clientForm, cnpj: e.target.value })}
                    placeholder="00.000.000/0000-00" required className="mt-1" data-testid="client-cnpj-input" />
                </div>
                <div>
                  <Label>Cidade <span className="text-red-600">*</span></Label>
                  <Input value={clientForm.city} onChange={(e) => setClientForm({ ...clientForm, city: e.target.value })}
                    required className="mt-1" data-testid="client-city-input" />
                </div>
                <div>
                  <Label>UF <span className="text-red-600">*</span></Label>
                  <Input value={clientForm.state} onChange={(e) => setClientForm({ ...clientForm, state: e.target.value.toUpperCase() })}
                    maxLength={2} required className="mt-1" placeholder="GO" data-testid="client-state-input" />
                </div>
                <div>
                  <Label>Contato (nome) <span className="text-red-600">*</span></Label>
                  <Input value={clientForm.contact_name} onChange={(e) => setClientForm({ ...clientForm, contact_name: e.target.value })} required className="mt-1" data-testid="client-contact-name-input" />
                </div>
                <div>
                  <Label>Contato (e-mail) <span className="text-red-600">*</span></Label>
                  <Input type="email" value={clientForm.contact_email} onChange={(e) => setClientForm({ ...clientForm, contact_email: e.target.value })} required className="mt-1" data-testid="client-contact-email-input" />
                </div>
                <div>
                  <Label>Contato (telefone) <span className="text-red-600">*</span></Label>
                  <Input value={clientForm.contact_phone} onChange={(e) => setClientForm({ ...clientForm, contact_phone: e.target.value })} required className="mt-1" placeholder="(00) 00000-0000" data-testid="client-contact-phone-input" />
                </div>

                <div className="sm:col-span-2 mt-2 p-4 border-2 border-dashed border-amber-300 rounded-lg bg-amber-50/40">
                  <Label className="text-sm font-semibold text-amber-900 flex items-center gap-2">
                    <Upload className="w-4 h-4" />
                    Contrato assinado <span className="text-red-600">*</span>
                  </Label>
                  <p className="text-xs text-slate-600 mt-1 mb-3">
                    Anexe o contrato assinado em PDF (máx 10MB). É obrigatório para concluir o cadastro.
                  </p>
                  {contractFilePreview ? (
                    <div className="flex items-center justify-between gap-2 p-3 bg-white border border-emerald-300 rounded-md">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                        <div>
                          <div className="text-sm font-medium">{contractFilePreview.name}</div>
                          <div className="text-xs text-slate-500">{contractFilePreview.size}</div>
                        </div>
                      </div>
                      <button type="button" onClick={() => { setContractFile(null); setContractFilePreview(null); }}
                        className="text-xs text-red-600 hover:underline">
                        Remover
                      </button>
                    </div>
                  ) : (
                    <Input type="file" accept=".pdf,.doc,.docx" onChange={handleFileChange} data-testid="contract-file-input" />
                  )}
                </div>

                <div className="sm:col-span-2 flex justify-end gap-2">
                  <Button type="submit" disabled={saving} className="bg-midnight hover:bg-ink text-white" data-testid="submit-client-button">
                    <Send className="w-4 h-4 mr-2" />
                    Cadastrar cliente e seguir
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contract">
          <Card className="card-soft">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="w-4 h-4 text-midnight" />
                Dados do Contrato
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={submitContract} className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label>Cliente <span className="text-red-600">*</span></Label>
                  <Select value={contractForm.client_id} onValueChange={(v) => setContractForm({ ...contractForm, client_id: v })}>
                    <SelectTrigger className="mt-1" data-testid="contract-client-select">
                      <SelectValue placeholder="Selecione o cliente" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name} — {c.city}/{c.state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label>Produto contratado <span className="text-red-600">*</span></Label>
                  <Select value={contractForm.product_id} onValueChange={(v) => setContractForm({ ...contractForm, product_id: v })}>
                    <SelectTrigger className="mt-1" data-testid="contract-product-select">
                      <SelectValue placeholder="Selecione o produto" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name} — {p.phases.length} fases / {p.duration_months} meses</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Data de início <span className="text-red-600">*</span></Label>
                  <Input type="date" value={contractForm.start_date} onChange={(e) => setContractForm({ ...contractForm, start_date: e.target.value })} required className="mt-1" />
                </div>
                <div>
                  <Label>Data de término <span className="text-red-600">*</span></Label>
                  <Input type="date" value={contractForm.end_date} onChange={(e) => setContractForm({ ...contractForm, end_date: e.target.value })} required className="mt-1" />
                </div>
                <div className="sm:col-span-2">
                  <Label>Valor do contrato (R$)</Label>
                  <Input type="number" step="0.01" value={contractForm.contract_value} onChange={(e) => setContractForm({ ...contractForm, contract_value: e.target.value })} className="mt-1" />
                </div>
                <div className="sm:col-span-2">
                  <Label>Observações</Label>
                  <Textarea value={contractForm.notes} onChange={(e) => setContractForm({ ...contractForm, notes: e.target.value })} rows={3} className="mt-1" />
                </div>
                <div className="sm:col-span-2 flex justify-end gap-2">
                  <Button type="submit" disabled={saving} className="bg-midnight hover:bg-ink text-white" data-testid="submit-contract-button">
                    <Send className="w-4 h-4 mr-2" />
                    Cadastrar contrato e encaminhar à Secretária
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="existing">
          <CadastrosManager
            initialClients={clients}
            onClientsChanged={(updated) => setClients(updated)}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
